import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  AppBar as NavBar,
  Tabs as TabsContainer,
  Tab,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import {
  ChallengeCardsContent,
  Container,
  Header,
  MainContent,
  NavigationMenu,
  RightSideContent,
  TitleWrapper,
  WrapperMainContent,
} from './styles';
import { BlogIcon, CardPlusIcon } from '../../../assets/svg/SVGComponents';
import FeedPost from '../FeedPost';
import api from '../../../services/api';
import IChallengeDataProps from '../../../dtos/IChallengeDataProps';
import ChallengeCardHorizontalSmall from '../../../components/ChallengeCard/ChallengeCardHorizontalSmall';
import LoadingStateRightSide from '../LoadingStateRightSide';
import { useModal } from '../../../hooks/modal';
import CommunityPost from '../../../modals/CommunityPostModal';
import { TutorialId } from '../../../hooks/onboarding';
import SectionTutorialButton from '../../../components/Onboarding/SectionTutorialButton';

interface CommunityParams {
  project_id?: string;
  [key: string]: string | undefined;
}

const CommunityPage: React.FC = () => {
  const { project_id } = useParams<CommunityParams>();
  const [selectedTab, setSelectedTab] = React.useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { handleModal } = useModal();
  const handleModalRef = useRef(handleModal);
  handleModalRef.current = handleModal;
  const [challengesData, setChallengesData] = useState<IChallengeDataProps[]>();
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });
  const [isLoading, setIsLoading] = useState(false);

  const isMobile = useMemo(
    () => isTabletOrMobile || isPortrait,
    [isTabletOrMobile, isPortrait],
  );

  useEffect(() => {
    const abortController = new AbortController();
    setIsLoading(true);

    async function fetchData(): Promise<void> {
      try {
        const response = await api.get('/challenges/available', {
          signal: abortController.signal,
        });
        setChallengesData(response.data);
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Failed to load challenges:', error);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      abortController.abort();
    };
  }, []);

  const handleSelectedTabChange = useCallback(
    (_event: React.ChangeEvent<{}>, tabIndex: number) => {
      setSelectedTab(tabIndex);
    },
    [],
  );

  useEffect(() => {
    if (project_id) {
      handleModalRef.current(<CommunityPost post_project_id={project_id} />, {
        pathname: '/community',
        state: {
          'modal-type': 'post',
          'modal-source': 'community',
        },
      });
    }
  }, [project_id]);
  return (
    <Container>
      <MainContent>
        <Header>
          <TitleWrapper>
            <BlogIcon
              width="2rem"
              stroke="#FF9800"
              strokeWidth={3}
              fill="none"
            />
            <h1>{t('community.title', 'Comunidade')}</h1>
            <SectionTutorialButton tutorialId={TutorialId.COMMUNITY} />
          </TitleWrapper>
          <PrimaryButton
            id="community-create-post"
            type="button"
            onClick={() => navigate('/create/post')}
          >
            <CardPlusIcon
              width="1.6rem"
              stroke="#FFffff"
              strokeWidth={2}
              fill="none"
            />
            <p>{t('community.publish_project', 'Publicar projeto')}</p>
          </PrimaryButton>
        </Header>

        <WrapperMainContent>
          <NavigationMenu>
            <NavBar position="relative" className="profile-navBar">
              <TabsContainer
                value={selectedTab}
                onChange={handleSelectedTabChange}
                className="profile-tabs-container"
                TabIndicatorProps={{
                  style: {
                    background: '#FF9800',
                    height: '5px',
                    borderRadius: '2.5px',
                    marginBottom: '-3px',
                    zIndex: 1101,
                  },
                }}
              >
                <Tab label={t('tabs.projects')} className="profile-tab-item" />
                <Tab
                  label={t('tabs.my_projects')}
                  className="profile-tab-item"
                />
              </TabsContainer>
            </NavBar>
            {selectedTab === 0 && <FeedPost type="projects" />}
            {selectedTab === 1 && <FeedPost type="my_projects" />}
          </NavigationMenu>
        </WrapperMainContent>
      </MainContent>
      {!isMobile && (
        <RightSideContent>
          {isLoading && <LoadingStateRightSide />}
          {!isLoading && (
            <>
              <h2>
                {t(
                  'community.challenge_suggestions',
                  'Sugestões de desafios para você',
                )}
              </h2>

              {challengesData && (
                <ChallengeCardsContent>
                  {challengesData.slice(0, 3).map(challenge => (
                    <ChallengeCardHorizontalSmall
                      key={challenge.id}
                      {...challenge}
                      type="submit"
                    />
                  ))}
                </ChallengeCardsContent>
              )}
            </>
          )}
        </RightSideContent>
      )}
    </Container>
  );
};

export default CommunityPage;
